// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui
import { Alert, Box, CircularProgress } from '@mui/material';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import type { ReadUnitDto } from '@/api/generated/recalc/endpoints/v1/units';
import {
  type GetAlarmConfigurationDto,
  useGetAlarmConfigurationQuery,
} from '@/api/generated/recalc/endpoints/v2/alarmConfiguration';
import {
  useGetV2AlarmTypesByIdQuery,
  useGetV2AlarmTypesQuery,
} from '@/api/generated/recalc/endpoints/v2/alarmTypes';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-components
import { EditQualityOfServiceDataStreamConfiguration } from '@/features/alarm-hub/components/alarm-configuration/forms/qualityOfServiceDataStream';
import { EditUsageOverTimeConfiguration } from '@/features/alarm-hub/components/alarm-configuration/forms/usageOverTime';

export const Route = createFileRoute(
  '/$accountId/_user/alarms/configuration/$alarmConfigurationId',
)({
  component: RouteComponent,
});

function renderAlarmConfigurationForm({
  accountId,
  alarmConfigurationId,
  alarmType,
  configurationSchema,
  alarmConfiguration,
  t,
  units,
}: {
  accountId: number;
  alarmConfigurationId: number;
  alarmType?: string;
  configurationSchema?: string | null;
  alarmConfiguration: GetAlarmConfigurationDto;
  t: (key: string, options?: Record<string, unknown>) => string;
  units: ReadUnitDto[];
}) {
  switch (alarmType) {
    case 'QualityOfServiceDataStream':
      return (
        <EditQualityOfServiceDataStreamConfiguration
          accountId={accountId}
          alarmConfigurationId={alarmConfigurationId}
          configurationSchema={configurationSchema}
          alarmConfiguration={alarmConfiguration}
          units={units ?? []}
        />
      );
    case 'UsageOverTime':
      return (
        <EditUsageOverTimeConfiguration
          accountId={accountId}
          alarmConfigurationId={alarmConfigurationId}
          configurationSchema={configurationSchema}
          alarmConfiguration={alarmConfiguration}
          units={units ?? []}
        />
      );
    default:
      return (
        <Alert severity="info">
          {t('type.notSupported', {
            defaultValue:
              'No dedicated edit form exists yet for this alarm type.',
          })}
        </Alert>
      );
  }
}

function RouteComponent() {
  const { t } = useTranslation('alarms');
  const { accountId, alarmConfigurationId } = Route.useParams();

  const alarmConfigurationIdAsNumber = Number(alarmConfigurationId);
  const accountIdAsNumber = Number(accountId);

  const {
    data: alarmConfiguration,
    isLoading: isLoadingAlarmConfiguration,
    isError: isAlarmConfigurationError,
  } = useGetAlarmConfigurationQuery(
    { id: alarmConfigurationIdAsNumber },
    { skip: Number.isNaN(alarmConfigurationIdAsNumber) },
  );

  const { data: units, isLoading: isLoadingUnits } =
    useUnitsGetCollectionAsyncByqueryOptionsQuery(
      { 'Account.AccountId': `eq(${accountIdAsNumber})` },
      { skip: !accountIdAsNumber },
    );

  const { data: alarmTypes = [] } = useGetV2AlarmTypesQuery({
    orderBy: 'name',
    orderDirection: 'asc',
  });

  const matchingAlarmType = alarmTypes.find(
    (alarmType) => alarmType.name === alarmConfiguration?.alarmType,
  );

  const normalizedAlarmTypeName = (alarmConfiguration?.alarmType ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

  const fuzzyMatchedAlarmType = alarmTypes.find(
    (alarmType) =>
      (alarmType.name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '') ===
      normalizedAlarmTypeName,
  );

  const alarmTypeId =
    matchingAlarmType?.alarmTypeId ?? fuzzyMatchedAlarmType?.alarmTypeId;

  const { data: alarmTypeById, isLoading: isLoadingAlarmTypeById } =
    useGetV2AlarmTypesByIdQuery(
      { id: alarmTypeId as number },
      { skip: typeof alarmTypeId !== 'number' },
    );

  const resolvedConfigurationSchema =
    alarmTypeById?.configurationSchema ??
    matchingAlarmType?.configurationSchema ??
    fuzzyMatchedAlarmType?.configurationSchema;

  if (isLoadingAlarmConfiguration || isLoadingAlarmTypeById || isLoadingUnits) {
    return (
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (isAlarmConfigurationError || !alarmConfiguration) {
    return (
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <Alert severity="error">
          {t('messages.alarm_configuration_load_failed', {
            defaultValue: 'Failed to load alarm configuration',
          })}
        </Alert>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="xl" sx={{ py: 2 }}>
      <PageHeader
        title={t('dialog.edit_alarm_configuration', {
          defaultValue: 'Edit Alarm Configuration',
        })}
        disableArrow={false}
      />

      {renderAlarmConfigurationForm({
        accountId: accountIdAsNumber,
        alarmConfigurationId: alarmConfigurationIdAsNumber,
        alarmType: alarmConfiguration.alarmType,
        configurationSchema: resolvedConfigurationSchema,
        alarmConfiguration,
        t,
        units: units ?? [],
      })}
    </PageContainer>
  );
}
